/* @flow */

import { useDispatch } from 'react-redux';
import {
  isBraindateCancelled,
  normalizeBraindate,
} from '@braindate/domain/lib/braindate/util';
import {
  getEventId,
  getEventSocketURL,
} from '@braindate/domain/lib/event/util';
import { areMembershipSlackNotificationsEnabled } from '@braindate/domain/lib/membership/util';
import { getUserId } from '@braindate/domain/lib/user/util';
import {
  topicModerationRejectedStatus,
  topicModerationVerifiedStatus,
} from '@braindate/domain/lib/topic/setting';
import { isObject } from '@braindate/util/lib/type';
import { TAG_TYPE } from 'src/shared/app/base/api/apiConstant';

import {
  braindateCreated,
  braindateChanged,
  braindateFollowup,
  groupBraindateFollowup,
  braindateReminder,
  groupBraindateReminder,
  braindateEarlyReminder,
  groupBraindateEarlyReminder,
  braindateCheckIn,
  calendarSyncStatusMessage,
  moderationState,
  notificationCountUpdate,
  fishbowlEarlyReminder,
  fishbowlReminder,
  engagement,
  expressionsReceiver,
} from 'src/shared/app/base/constant/socketConstants';
import useSocket from 'src/shared/app/base/hook/useSocket';
import { showVirtualBraindateEarlyReminder } from 'plugin/virtual-braindate/action/uiVirtualBraindateActions';
import useVirtualBraindate from 'plugin/virtual-braindate/hook/useVirtualBraindate';
import { showCheckInReminder } from 'src/shared/app/check-in/action/uiCheckInActions';
import { getBraindateReminderNotificationId } from 'src/shared/app/check-in/util/checkInUtil';
import useFeatureDecisions from 'src/shared/app/feature/hook/useFeatureDecisions';
import {
  showModerationAcceptedBanner,
  showModerationRejectedBanner,
  showModerationAcceptedAndSlackBanner,
} from 'src/shared/app/moderation/action/moderationActions';
import { dismissNotification } from 'src/shared/app/notification/action/uiNotificationActions';
import { showPostBraindateFeedback } from 'src/shared/app/post-braindate/action/postBraindateActions';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import { createBraindate } from 'src/shared/domain/braindate/action/braindateActions';
import useEvent from 'src/shared/domain/event/hook/useEvent';
import { updateCalendarSyncStatus } from 'src/shared/domain/membership/action/membershipActions';
import useMembership from 'src/shared/domain/membership/hook/useMembership';
import { patchTopicModeration } from 'src/shared/domain/topic/action/topicActions';
import { fetchUserActivity } from 'src/shared/domain/user/action/userActions';
import useSelf from 'src/shared/domain/user/hook/useSelf';
import { showEngagementModal } from 'src/shared/app/topic/new/action/appNewTopicActions';
import { setUnreadNotificationsCount } from 'src/shared/app/notification/reducer/slices/appUnreadCountNotificationSlice';
import { showExpressionOfGratitudeRecipientModal } from 'src/shared/app/expression-of-gratitude/action/expressionOfGratitudeAction';

import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { ExpressionOfGratitude } from '@braindate/domain/lib/expressionOfGratitude/type';
import { invalidateTags } from 'src/shared/app/base/api/actions/apiActions';

export function useDispatchNotification() {
  const event = useEvent();
  const user = useSelf();
  const membership = useMembership();
  const dispatch = useDispatch();
  const isVirtualBraindateEnabled = useVirtualBraindate();
  const { isSelfCheckInEnabled, isSlackIntegrationEnabled } =
    useFeatureDecisions();
  // FIXME: don't hardcode `urls`

  const handleNotification = ({
    trigger,
    data,
  }: {
    trigger: string,
    data: Object,
  }) => {
    switch (trigger) {
      case notificationCountUpdate:
        dispatch(setUnreadNotificationsCount(data.value));
        dispatch(invalidateTags(TAG_TYPE.NOTIFICATION));

        break;

      case braindateCreated: {
        const braindate = (data: Braindate);

        // State update

        // Add the braindate data to the state and update it in `app.braindates.all.result`
        // so that the notification count is updated
        // FIXME: handle notifications through another socket message
        dispatch(createBraindate(braindate));

        break;
      }

      case braindateChanged: {
        const braindate = (data: Braindate);

        // State update

        dispatch(patchEntities(normalizeBraindate(braindate).entities));

        // Discard check-in notification if braindate was cancelled
        if (isBraindateCancelled(braindate)) {
          dispatch(
            dismissNotification(getBraindateReminderNotificationId(braindate)),
          );
        }

        break;
      }
      case fishbowlReminder:
      case groupBraindateReminder:
      case braindateReminder: {
        const braindate: Braindate = (data: Braindate);

        if (isSelfCheckInEnabled || isVirtualBraindateEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showCheckInReminder(braindate));
        }

        break;
      }

      // Every early reminder are only for virtual braindates
      case braindateEarlyReminder: {
        const braindate: Braindate = (data: Braindate);

        if (isVirtualBraindateEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showVirtualBraindateEarlyReminder(braindate));
        }

        break;
      }

      case groupBraindateEarlyReminder: {
        const braindate: Braindate = (data: Braindate);

        if (isVirtualBraindateEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showVirtualBraindateEarlyReminder(braindate));
        }

        break;
      }

      case fishbowlEarlyReminder: {
        const braindate: Braindate = (data: Braindate);

        dispatch(patchEntities(normalizeBraindate(braindate).entities));
        dispatch(showVirtualBraindateEarlyReminder(braindate));
        break;
      }

      // Someone else has checked in
      case braindateCheckIn: {
        const braindate = (data: Braindate);

        dispatch(patchEntities(normalizeBraindate(braindate).entities));

        break;
      }

      case calendarSyncStatusMessage: {
        dispatch(updateCalendarSyncStatus(isObject(data) ? data : {}));
        if (user) {
          // Fetch update authenticated user's activity
          dispatch(fetchUserActivity(getEventId(event), getUserId(user)));
        }

        break;
      }

      case moderationState: {
        const { state } = data;

        // If the topic status is rejected, we show the banner and patch the topic status and reason
        // If the topic is accepted, we check is slack is enabled to display the proper notification:
        // Posted or posted and shared on slack

        if (state === topicModerationRejectedStatus) {
          dispatch(patchTopicModeration(data));
          dispatch(showModerationRejectedBanner());
        } else if (state === topicModerationVerifiedStatus && membership) {
          if (
            isSlackIntegrationEnabled &&
            areMembershipSlackNotificationsEnabled(membership)
          ) {
            dispatch(showModerationAcceptedAndSlackBanner());
          } else {
            dispatch(showModerationAcceptedBanner());
          }
        }

        break;
      }

      case braindateFollowup:
      case groupBraindateFollowup: {
        const braindate = (data: Braindate);
        dispatch(showPostBraindateFeedback(braindate));

        break;
      }

      case engagement: {
        const engagementType = (data: string);
        dispatch(showEngagementModal(engagementType));

        break;
      }
      case expressionsReceiver: {
        const expressionOfGratitude = (data: ExpressionOfGratitude);

        dispatch(
          showExpressionOfGratitudeRecipientModal(expressionOfGratitude),
        );

        break;
      }
    }
  };

  return handleNotification;
}

export default function useGlobalNotifications() {
  const event = useEvent();
  const handleNotification = useDispatchNotification();
  // FIXME: don't hardcode `urls`
  const url = 'urls' in event ? getEventSocketURL(event) : '';

  useSocket(url, handleNotification);
}
